/**
 * API client with SDK integration
 *
 * Uses the auth SDK to get access tokens for authenticated requests.
 */

/**
 * Get dynamic API URL based on hostname
 */
function getApiUrl(): string {
  const hostname = window.location.hostname

  // Production pattern: account.{slug}.u.software -> api.{slug}.u.software
  const prodMatch = hostname.match(/^account\.(.+)\.u\.software$/)
  if (prodMatch) {
    const orgSlug = prodMatch[1]
    return `https://api.${orgSlug}.u.software`
  }

  // Alternative pattern: {slug}.account.u.software -> {slug}.api.u.software
  const altMatch = hostname.match(/^(.+)\.account\.u\.software$/)
  if (altMatch) {
    const orgSlug = altMatch[1]
    return `https://${orgSlug}.api.u.software`
  }

  // Main u.software: account.u.software -> api.u.software
  if (hostname === 'account.u.software') {
    return 'https://api.u.software'
  }

  // Fly.io deployment
  if (hostname === 'usoftware-account.fly.dev') {
    return 'https://usoftware-api.fly.dev'
  }

  // Development fallback - only for actual localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_API_URL || 'http://localhost:8080'
  }

  // Unknown domain - use fly.dev defaults
  console.warn(`Unknown hostname for API: ${hostname}, using fly.dev`)
  return 'https://usoftware-api.fly.dev'
}

const API_URL = getApiUrl()

/**
 * Check if current context is an org-scoped subdomain (OrgUser)
 */
function isOrgContext(): boolean {
  const hostname = window.location.hostname
  // account.{slug}.u.software or {slug}.account.u.software patterns
  const prodMatch = hostname.match(/^account\.(.+)\.u\.software$/)
  const altMatch = hostname.match(/^(.+)\.account\.u\.software$/)
  return !!(prodMatch || altMatch)
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>
  post<T>(path: string, body?: unknown): Promise<T>
  patch<T>(path: string, body: unknown): Promise<T>
  put<T>(path: string, body: unknown): Promise<T>
  delete<T>(path: string): Promise<T>
}

export function createApiClient(getAccessToken: () => Promise<string | null>): ApiClient {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await getAccessToken()

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      let errorData: { code?: string; message?: string; details?: Record<string, unknown> } = {}
      try {
        errorData = await response.json()
      } catch {
        // Ignore JSON parse errors
      }
      throw new ApiError(
        response.status,
        errorData.code || 'UNKNOWN_ERROR',
        errorData.message || `Request failed with status ${response.status}`,
        errorData.details
      )
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    return response.json()
  }

  return {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
    put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
  }
}

// API types

export interface User {
  id: string
  email: string
  email_verified: boolean
  username: string | null
  display_name: string | null
  avatar_url: string | null
  created_at: string
}

// API response wrappers (backend wraps responses)
interface UserResponse {
  user: User
}

interface UpdateProfileResponse {
  user: User
  message: string
}

export interface Session {
  id: string
  user_id: string
  device_name: string | null
  device_type: string | null
  browser: string | null
  os: string | null
  ip_address: string | null
  auth_method: string
  auth_level: string
  remember_me: boolean
  created_at: string
  last_active_at: string
  expires_at: string
  is_current: boolean
}

export interface MFAFactor {
  id: string
  factor_type: 'totp' | 'passkey'
  name: string | null
  verified: boolean
  created_at: string
  last_used_at: string | null
}

export interface TOTPSetupResponse {
  factor_id: string
  secret: string
  provisioning_uri: string
  qr_code_base64: string | null
}

export interface LinkedIdentity {
  id: string
  provider: 'google' | 'github' | 'discord' | 'twitter' | 'telegram'
  provider_user_id: string
  email: string | null
  email_verified: boolean
  display_name: string | null
  avatar_url: string | null
  created_at: string
  updated_at: string
}

// App types

export interface AppInfo {
  id: string
  name: string
  slug: string
  logo_url: string | null
  homepage_url: string | null
}

export interface ScopeInfo {
  name: string
  description: string
}

export interface AuthorizedApp {
  app: AppInfo
  app_user_id: string
  scopes: string[]
  authorized_at: string
  last_used_at: string | null
}

export interface AuthorizedAppDetail {
  app: AppInfo
  app_user_id: string
  scopes: ScopeInfo[]
  roles: string[]
  authorized_at: string
  last_used_at: string | null
  api_request_count: number
}

export interface ActivityItem {
  id: string
  action: string
  description: string | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
  // Additional fields from audit log
  user_id?: string
  app_id?: string | null
  app_name?: string | null
  org_id?: string | null
  org_name?: string | null
  target_type?: string | null
  target_id?: string | null
  metadata?: Record<string, unknown> | null
}

// API Key types (Phase 13)

export interface APIKey {
  id: string
  name: string
  key_prefix: string
  scopes: string[]
  status: 'active' | 'revoked' | 'expired'
  created_at: string
  expires_at: string | null
  last_used_at: string | null
  use_count: number
}

export interface APIKeyCreated extends APIKey {
  raw_key: string
}

// Billing types (Phase 15)

export interface PaymentMethod {
  id: string
  processor: 'stripe' | 'paypal'
  method_type: string
  display_name: string | null
  last_four: string | null
  expiry_month: number | null
  expiry_year: number | null
  brand: string | null
  payer_detail: string | null  // email for PayPal, last4 for cards
  payer_name: string | null
  account_status: string | null
  processor_data: Record<string, any> | null  // Raw processor data
  is_default: boolean
  status: 'active' | 'expired' | 'failed'
  created_at: string
}

export interface Receipt {
  id: string
  charge_id: string
  receipt_number: string
  amount_cents: number
  currency: string
  status: string
  app_name: string | null
  description: string | null
  issued_at: string
  pdf_url: string | null
}

// Organization types (Phase 16)

export interface Organization {
  id: string
  slug: string
  name: string
  description: string | null
  logo_url: string | null
  owner_id: string
  member_count: number
  app_count: number
  created_at: string
}

export interface OrgMember {
  id: string
  user_id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  role: string
  role_name: string
  joined_at: string
  is_owner: boolean
}

export interface OrgInvitation {
  id: string
  email: string
  role: string
  role_name: string
  invited_by: string
  invited_at: string
  expires_at: string
}

export interface OrgRole {
  id: string
  name: string
  description: string | null
  scopes: string[]
  is_system: boolean
  member_count: number
}

export interface OrgActivityItem {
  id: string
  action: string
  actor_name: string
  target_name: string | null
  details: string | null
  created_at: string
}

export interface AccountSummary {
  email: string
  email_verified: boolean
  mfa_enabled: boolean
  mfa_method: string | null
  active_sessions: number
  linked_accounts: number
  authorized_apps: number
}

// App management types (Phase 17)

export interface OrgApp {
  id: string
  slug: string
  name: string
  description: string | null
  logo_url: string | null
  homepage_url: string | null
  user_count: number
  status: 'active' | 'inactive' | 'suspended'
  created_at: string
}

export interface OrgAppDetail extends OrgApp {
  client_id: string
  client_secret_preview: string
  user_count: number
  request_count: number
  revenue_cents: number
  last_synced_at: string | null
}

export interface OrgAppCreated extends OrgAppDetail {
  client_secret: string
}

export interface CreateAppData {
  name: string
  slug: string
  description?: string
  homepage_url?: string
}

export interface UpdateAppData {
  name?: string
  description?: string
  logo_url?: string
  homepage_url?: string
  status?: 'active' | 'inactive'
}

export interface AppOAuthSettings {
  redirect_uris: string[]
  allowed_origins: string[]
  access_token_ttl: number
  refresh_token_ttl: number
  pkce_required: boolean
}

export interface UpdateOAuthData {
  redirect_uris?: string[]
  allowed_origins?: string[]
  access_token_ttl?: number
  refresh_token_ttl?: number
  pkce_required?: boolean
}

export interface AppScope {
  name: string
  description: string
  last_synced_at: string
}

export interface AppRole {
  name: string
  description: string | null
  scopes: string[]
  user_count: number
  last_synced_at: string
}

export interface AppScopesData {
  scopes: AppScope[]
  roles: AppRole[]
  everyone_scopes: string[]
  last_synced_at: string | null
}

export interface AppWebhook {
  id: string
  url: string
  events: string[]
  status: 'active' | 'inactive' | 'failing'
  last_delivery_at: string | null
  last_response_code: number | null
  created_at: string
}

export interface CreateWebhookData {
  url: string
  events: string[]
}

export interface UpdateWebhookData {
  url?: string
  events?: string[]
  status?: 'active' | 'inactive'
}

export interface WebhookTestResult {
  success: boolean
  response_code: number | null
  response_time_ms: number | null
  error: string | null
}

export interface WebhookLog {
  id: string
  event_type: string
  response_code: number | null
  response_time_ms: number | null
  success: boolean
  error: string | null
  delivered_at: string
}

export interface AppAPIKeySettings {
  enabled: boolean
  max_keys_per_user: number
  default_scopes: string[]
  allowed_scopes: string[]
}

export interface UpdateAPIKeySettingsData {
  enabled?: boolean
  max_keys_per_user?: number
  default_scopes?: string[]
  allowed_scopes?: string[]
}

export interface AppUserInfo {
  id: string
  user_id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  roles: string[]
  scopes: string[]
  last_active_at: string | null
  created_at: string
}

export interface AppUserDetail extends AppUserInfo {
  activity: AppUserActivity[]
  api_keys_count: number
  total_requests: number
}

export interface AppUserActivity {
  id: string
  action: string
  description: string
  timestamp: string
}

// API functions

export const api = {
  // Profile
  getMe: async (client: ApiClient): Promise<User> => {
    const response = await client.get<UserResponse>('/api/me')
    return response.user
  },
  updateMe: async (client: ApiClient, data: { display_name?: string; username?: string }): Promise<User> => {
    const response = await client.patch<UpdateProfileResponse>('/api/me', data)
    return response.user
  },

  removeAvatar: async (client: ApiClient): Promise<User> => {
    const response = await client.delete<UserResponse>('/api/me/avatar')
    return response.user
  },

  uploadAvatar: async (client: ApiClient, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch('/api/me/upload/avatar', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })
    if (!response.ok) throw new Error('Failed to upload avatar')
    return response.json() as Promise<{ url: string }>
  },

  // Email
  requestEmailChange: (client: ApiClient, data: { new_email: string; password: string }) =>
    client.put<{ message: string }>('/api/me/email', data),

  // Password
  changePassword: (client: ApiClient, data: { current_password: string; new_password: string }) =>
    client.put<void>('/api/me/password', data),
  setPassword: (client: ApiClient, data: { password: string }) =>
    client.post<void>('/api/me/password', data),

  // Sessions
  getSessions: async (client: ApiClient) => {
    const res = await client.get<{ sessions: Session[] }>('/api/me/sessions')
    return res.sessions
  },
  revokeSession: (client: ApiClient, sessionId: string) =>
    client.delete<void>(`/api/me/sessions/${sessionId}`),
  revokeAllOtherSessions: (client: ApiClient) =>
    client.delete<void>('/api/me/sessions'),

  // MFA
  getMFAFactors: async (client: ApiClient) => {
    const res = await client.get<{ factors: MFAFactor[]; mfa_enabled: boolean }>('/api/me/mfa')
    return res.factors
  },
  beginTOTPSetup: (client: ApiClient, data?: { name?: string }) =>
    client.post<TOTPSetupResponse>('/api/me/mfa/totp', data || {}),
  verifyTOTPSetup: (client: ApiClient, data: { factor_id: string; code: string }) =>
    client.post<MFAFactor>('/api/me/mfa/totp/verify', data),
  removeMFAFactor: (client: ApiClient, factorId: string) =>
    client.delete<void>(`/api/me/mfa/${factorId}`),
  getRecoveryCodes: (client: ApiClient) => client.get<{ codes: string[] }>('/api/me/mfa/recovery-codes'),
  regenerateRecoveryCodes: (client: ApiClient) =>
    client.post<{ codes: string[] }>('/api/me/mfa/recovery-codes'),

  // Linked accounts
  getIdentities: async (client: ApiClient) => {
    const res = await client.get<{ identities: LinkedIdentity[] }>('/api/me/identities')
    return res.identities
  },
  unlinkIdentity: (client: ApiClient, identityId: string) =>
    client.delete<void>(`/api/me/identities/${identityId}`),

  // Apps (Phase 11)
  getApps: (client: ApiClient) => client.get<{ apps: AuthorizedApp[] }>('/api/me/apps'),
  getApp: (client: ApiClient, appSlug: string) =>
    client.get<{ app: AuthorizedAppDetail }>(`/api/me/apps/${appSlug}`),
  revokeApp: (client: ApiClient, appSlug: string) =>
    client.delete<void>(`/api/me/apps/${appSlug}`),

  // Activity and summary (Phase 11)
  getActivity: (client: ApiClient, limit?: number) => {
    // Use org endpoint for OrgUsers, regular endpoint for Users
    const endpoint = isOrgContext() ? '/api/audit/org/me/activity' : '/api/audit/me/activity'
    return client.get<{ items: ActivityItem[] }>(`${endpoint}${limit ? `?page_size=${limit}` : ''}`)
  },
  getSummary: (client: ApiClient) => client.get<AccountSummary>('/api/me/summary'),
  exportActivity: (client: ApiClient, format: 'json' | 'csv' = 'json') =>
    client.get<Blob>(`/api/me/activity/export?format=${format}`),

  // API Keys (Phase 13)
  getAPIKeys: (client: ApiClient, appId: string) =>
    client.get<{ keys: APIKey[] }>(`/api/me/apps/${appId}/keys`),
  getAPIKey: (client: ApiClient, appId: string, keyId: string) =>
    client.get<APIKey>(`/api/me/apps/${appId}/keys/${keyId}`),
  createAPIKey: (client: ApiClient, appId: string, data: { name: string; scopes: string[]; expires_in_days?: number }) =>
    client.post<APIKeyCreated>(`/api/me/apps/${appId}/keys`, data),
  revokeAPIKey: (client: ApiClient, appId: string, keyId: string) =>
    client.delete<void>(`/api/me/apps/${appId}/keys/${keyId}`),

  // Billing (Phase 15)
  getPaymentMethods: (client: ApiClient) =>
    client.get<{ payment_methods: PaymentMethod[] }>('/api/me/payment-methods'),
  addPaymentMethod: (client: ApiClient, data: { processor: string; processor_token: string; set_as_default?: boolean }) =>
    client.post<PaymentMethod>('/api/me/payment-methods', data),
  removePaymentMethod: (client: ApiClient, methodId: string) =>
    client.delete<void>(`/api/me/payment-methods/${methodId}`),
  setDefaultPaymentMethod: (client: ApiClient, methodId: string) =>
    client.put<PaymentMethod>(`/api/me/payment-methods/${methodId}/default`, {}),
  getReceipts: (client: ApiClient, limit?: number, offset?: number, appId?: string) =>
    client.get<{ receipts: Receipt[] }>(
      `/api/me/receipts?${new URLSearchParams({
        ...(limit && { limit: String(limit) }),
        ...(offset && { offset: String(offset) }),
        ...(appId && { app_id: appId }),
      }).toString()}`
    ),
  getReceipt: (client: ApiClient, receiptId: string) =>
    client.get<Receipt>(`/api/me/receipts/${receiptId}`),

  // Organizations (Phase 16)
  getOrganizations: (client: ApiClient) =>
    client.get<{ organizations: Organization[] }>('/api/me/organizations'),
  getOrganization: (client: ApiClient, orgId: string) =>
    client.get<Organization>(`/api/organizations/${orgId}`),
  createOrganization: (client: ApiClient, data: { name: string; slug: string; description?: string }) =>
    client.post<Organization>('/api/organizations', data),
  updateOrganization: (client: ApiClient, orgId: string, data: { name?: string; description?: string }) =>
    client.patch<Organization>(`/api/organizations/${orgId}`, data),
  deleteOrganization: (client: ApiClient, orgId: string) =>
    client.delete<void>(`/api/organizations/${orgId}`),

  // Org members
  getOrgMembers: (client: ApiClient, orgId: string) =>
    client.get<{ members: OrgMember[] }>(`/api/organizations/${orgId}/members`),
  inviteOrgMember: (client: ApiClient, orgId: string, data: { email: string; role: string }) =>
    client.post<OrgInvitation>(`/api/organizations/${orgId}/invitations`, data),
  updateOrgMember: (client: ApiClient, orgId: string, memberId: string, data: { role: string }) =>
    client.patch<OrgMember>(`/api/organizations/${orgId}/members/${memberId}`, data),
  removeOrgMember: (client: ApiClient, orgId: string, memberId: string) =>
    client.delete<void>(`/api/organizations/${orgId}/members/${memberId}`),

  // Org invitations
  getOrgInvitations: (client: ApiClient, orgId: string) =>
    client.get<{ invitations: OrgInvitation[] }>(`/api/organizations/${orgId}/invitations`),
  cancelOrgInvitation: (client: ApiClient, orgId: string, invitationId: string) =>
    client.delete<void>(`/api/organizations/${orgId}/invitations/${invitationId}`),
  resendOrgInvitation: (client: ApiClient, orgId: string, invitationId: string) =>
    client.post<OrgInvitation>(`/api/organizations/${orgId}/invitations/${invitationId}/resend`),

  // Org roles
  getOrgRoles: (client: ApiClient, orgId: string) =>
    client.get<{ roles: OrgRole[] }>(`/api/organizations/${orgId}/roles`),
  createOrgRole: (client: ApiClient, orgId: string, data: { name: string; description?: string; scopes: string[] }) =>
    client.post<OrgRole>(`/api/organizations/${orgId}/roles`, data),
  updateOrgRole: (client: ApiClient, orgId: string, roleId: string, data: { name?: string; description?: string; scopes?: string[] }) =>
    client.patch<OrgRole>(`/api/organizations/${orgId}/roles/${roleId}`, data),
  deleteOrgRole: (client: ApiClient, orgId: string, roleId: string) =>
    client.delete<void>(`/api/organizations/${orgId}/roles/${roleId}`),

  // Org activity
  getOrgActivity: (client: ApiClient, orgId: string, limit?: number) =>
    client.get<{ items: OrgActivityItem[] }>(`/api/organizations/${orgId}/activity${limit ? `?limit=${limit}` : ''}`),

  // Org apps (Phase 17)
  getOrgApps: (client: ApiClient, orgId: string) =>
    client.get<{ apps: OrgApp[] }>(`/api/organizations/${orgId}/apps`),
  getOrgApp: (client: ApiClient, orgId: string, appId: string) =>
    client.get<OrgAppDetail>(`/api/organizations/${orgId}/apps/${appId}`),
  createOrgApp: (client: ApiClient, orgId: string, data: CreateAppData) =>
    client.post<OrgAppCreated>(`/api/organizations/${orgId}/apps`, data),
  updateOrgApp: (client: ApiClient, orgId: string, appId: string, data: UpdateAppData) =>
    client.patch<OrgApp>(`/api/organizations/${orgId}/apps/${appId}`, data),
  deleteOrgApp: (client: ApiClient, orgId: string, appId: string) =>
    client.delete<void>(`/api/organizations/${orgId}/apps/${appId}`),

  // App OAuth settings
  getAppOAuth: (client: ApiClient, orgId: string, appId: string) =>
    client.get<AppOAuthSettings>(`/api/organizations/${orgId}/apps/${appId}/oauth`),
  updateAppOAuth: (client: ApiClient, orgId: string, appId: string, data: UpdateOAuthData) =>
    client.patch<AppOAuthSettings>(`/api/organizations/${orgId}/apps/${appId}/oauth`, data),
  rotateClientSecret: (client: ApiClient, orgId: string, appId: string) =>
    client.post<{ client_secret: string }>(`/api/organizations/${orgId}/apps/${appId}/rotate-secret`),

  // App scopes and roles
  getAppScopes: (client: ApiClient, orgId: string, appId: string) =>
    client.get<AppScopesData>(`/api/organizations/${orgId}/apps/${appId}/scopes`),
  updateEveryoneScopes: (client: ApiClient, orgId: string, appId: string, data: { scopes: string[] }) =>
    client.put<{ scopes: string[] }>(`/api/organizations/${orgId}/apps/${appId}/everyone-scopes`, data),
  syncAppScopes: (client: ApiClient, orgId: string, appId: string) =>
    client.post<AppScopesData>(`/api/organizations/${orgId}/apps/${appId}/scopes/sync`),

  // App webhooks
  getAppWebhooks: (client: ApiClient, orgId: string, appId: string) =>
    client.get<{ webhooks: AppWebhook[]; webhook_secret: string }>(`/api/organizations/${orgId}/apps/${appId}/webhooks`),
  createAppWebhook: (client: ApiClient, orgId: string, appId: string, data: CreateWebhookData) =>
    client.post<AppWebhook>(`/api/organizations/${orgId}/apps/${appId}/webhooks`, data),
  updateAppWebhook: (client: ApiClient, orgId: string, appId: string, webhookId: string, data: UpdateWebhookData) =>
    client.patch<AppWebhook>(`/api/organizations/${orgId}/apps/${appId}/webhooks/${webhookId}`, data),
  deleteAppWebhook: (client: ApiClient, orgId: string, appId: string, webhookId: string) =>
    client.delete<void>(`/api/organizations/${orgId}/apps/${appId}/webhooks/${webhookId}`),
  testAppWebhook: (client: ApiClient, orgId: string, appId: string, webhookId: string) =>
    client.post<WebhookTestResult>(`/api/organizations/${orgId}/apps/${appId}/webhooks/${webhookId}/test`),
  getWebhookLogs: (client: ApiClient, orgId: string, appId: string, webhookId: string, limit?: number) =>
    client.get<{ logs: WebhookLog[] }>(`/api/organizations/${orgId}/apps/${appId}/webhooks/${webhookId}/logs${limit ? `?limit=${limit}` : ''}`),
  regenerateWebhookSecret: (client: ApiClient, orgId: string, appId: string) =>
    client.post<{ webhook_secret: string }>(`/api/organizations/${orgId}/apps/${appId}/webhooks/regenerate-secret`),

  // App API key settings
  getAppAPIKeySettings: (client: ApiClient, orgId: string, appId: string) =>
    client.get<AppAPIKeySettings>(`/api/organizations/${orgId}/apps/${appId}/api-keys`),
  updateAppAPIKeySettings: (client: ApiClient, orgId: string, appId: string, data: UpdateAPIKeySettingsData) =>
    client.patch<AppAPIKeySettings>(`/api/organizations/${orgId}/apps/${appId}/api-keys`, data),

  // App users
  getAppUsers: (client: ApiClient, orgId: string, appId: string, options?: { limit?: number; offset?: number; search?: string }) =>
    client.get<{ users: AppUserInfo[]; total: number }>(
      `/api/organizations/${orgId}/apps/${appId}/users?${new URLSearchParams({
        ...(options?.limit && { limit: String(options.limit) }),
        ...(options?.offset && { offset: String(options.offset) }),
        ...(options?.search && { search: options.search }),
      }).toString()}`
    ),
  getAppUser: (client: ApiClient, orgId: string, appId: string, userId: string) =>
    client.get<AppUserDetail>(`/api/organizations/${orgId}/apps/${appId}/users/${userId}`),
  updateAppUserRoles: (client: ApiClient, orgId: string, appId: string, userId: string, data: { roles: string[] }) =>
    client.put<AppUserInfo>(`/api/organizations/${orgId}/apps/${appId}/users/${userId}/roles`, data),

  // App audit log
  getAppAuditLog: (client: ApiClient, orgId: string, appId: string, options?: { action?: string; page?: number; page_size?: number }) =>
    client.get<{ items: AppUserActivity[]; total: number; page: number; page_size: number; has_more: boolean }>(
      `/api/organizations/${orgId}/apps/${appId}/audit-log?${new URLSearchParams({
        ...(options?.action && { action: options.action }),
        ...(options?.page && { page: String(options.page) }),
        ...(options?.page_size && { page_size: String(options.page_size) }),
      }).toString()}`
    ),

  // App analytics (placeholder endpoints)
  getAppAnalyticsOverview: (client: ApiClient, orgId: string, appId: string, days?: number) =>
    client.get<unknown>(`/api/organizations/${orgId}/apps/${appId}/analytics/overview?days=${days || 30}`),
  getAppRevenueBreakdown: (client: ApiClient, orgId: string, appId: string, days?: number) =>
    client.get<unknown>(`/api/organizations/${orgId}/apps/${appId}/analytics/revenue?days=${days || 30}`),
  getAppUserAnalytics: (client: ApiClient, orgId: string, appId: string, days?: number) =>
    client.get<unknown>(`/api/organizations/${orgId}/apps/${appId}/analytics/users?days=${days || 30}`),
}
